import { useState, useRef } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in nested directory
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import type { IReceiptUploadDialogProps } from '@/@types/payment';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated

export function ReceiptUploadDialog({
  open,
  onOpenChange,
  invoiceId,
  companyId,
}: IReceiptUploadDialogProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const createReceipt = useMutation(api.payments.mutations.create);

  const handleSubmit = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    try {
      // Step 1: Get upload URL
      const uploadUrl = await createUploadUrl();

      // Step 2: Upload file
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      const { storageId } = (await response.json()) as { storageId: Id<'_storage'> };

      // Step 3: Create receipt record
      await createReceipt({
        companyId,
        invoiceId,
        storageId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        notes: notes.trim() || undefined,
      });

      toast.success('Receipt uploaded successfully');
      setNotes('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload receipt');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Payment Receipt</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="receipt-file">Receipt File</Label>
            <input
              ref={fileInputRef}
              id="receipt-file"
              type="file"
              accept="image/*,application/pdf"
              className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
            />
            <p className="text-xs text-muted-foreground">
              Accepted: Images (JPG, PNG) and PDF files
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="receipt-notes">Notes (optional)</Label>
            <Textarea
              id="receipt-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any notes about this payment..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload aria-hidden="true" className="w-4 h-4 mr-2" />
                Upload Receipt
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
